"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { SlidingNumber } from "../../../../components/motion-primitives/sliding-number.tsx";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import { cn } from "@/lib/utils.ts";

interface ScreenShareViewProps {
  participant: any; // Cloudflare participant object
  compact?: boolean;
}

// VideoPlayer component for rendering screen share video tracks
function ScreenShareVideoPlayer({
  track,
  isLocal = false,
}: {
  track: MediaStreamTrack | null;
  isLocal?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && track) {
      // Create a new stream and add the track to it
      const mediaStream = new MediaStream([track]);

      videoRef.current.srcObject = mediaStream;

      // iPad-specific optimizations
      if (videoRef.current) {
        // Ensure autoplay works on iPad
        videoRef.current.setAttribute("webkit-playsinline", "true");
        videoRef.current.setAttribute("playsinline", "true");

        // Force play on iPad
        const playPromise = videoRef.current.play();

        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.log("Screen share video play failed:", error);
          });
        }
      }
    } else if (videoRef.current) {
      // Clear the stream if the track is removed
      videoRef.current.srcObject = null;
    }
  }, [track]);

  return (
    <>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="h-full w-full object-contain"
        muted={isLocal} // Mute local screen share to avoid feedback
      />
    </>
  );
}

// AudioPlayer component for rendering screen share audio tracks
function ScreenShareAudioPlayer({
  track,
  volume = 1.0,
}: {
  track: MediaStreamTrack | null;
  volume?: number;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (audioRef.current && track) {
      // Create a new stream and add the track to it
      const mediaStream = new MediaStream([track]);

      audioRef.current.srcObject = mediaStream;
    } else if (audioRef.current) {
      // Clear the stream if the track is removed
      audioRef.current.srcObject = null;
    }
  }, [track]);
  useEffect(() => {
    if (audioRef.current && track) {
      if (volume === 0) {
        audioRef.current.muted = true;
      } else {
        audioRef.current.volume = volume;
        audioRef.current.muted = false;
      }
    }
  }, [volume]);

  return <audio ref={audioRef} autoPlay />;
}

export default function ScreenShareView({ participant, compact }: ScreenShareViewProps) {
  const { t } = useTranslation(["common", "classroom"]);
  const { user } = useUserStore();

  // Volume control state for screen share audio
  const [screenShareVolume, setScreenShareVolume] = useState(1.0);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  if (!participant) {
    return null; // Participant has left or doesn't exist
  }

  // Extract participant properties with defensive access to handle Cloudflare data structure
  const displayName = participant.name || t("common:participant");
  const isLocal = participant.customParticipantId === user?.id;
  const screenShareOn = participant.screenShareEnabled;
  const screenShareVideoTrack = participant.screenShareTracks.video;
  const screenShareAudioTrack = participant.screenShareTracks.audio;

  const handleVolumeChange = (newVolume: number[]) => {
    const volume = newVolume[0];

    setScreenShareVolume(volume);
  };

  const toggleMute = () => {
    const newVolume = screenShareVolume > 0 ? 0 : 1.0;

    setScreenShareVolume(newVolume);
  };

  return (
    <div className="relative h-full w-full overflow-hidden rounded-lg bg-black">
      {/* Screen Share Video */}
      {screenShareOn && screenShareVideoTrack ? (
        <ScreenShareVideoPlayer isLocal={isLocal} track={screenShareVideoTrack} />
      ) : (
        <div className="bg-side-accent flex h-full w-full items-center justify-center">
          <div className="text-center">
            <Icon
              className="text-muted-foreground mx-auto mb-2"
              height="48"
              icon="lucide:monitor-off"
              width="48"
            />
            <p className="text-muted-foreground text-sm">{t("classroom:screenShareInUse")}</p>
          </div>
        </div>
      )}

      {/* Screen Share Audio - hidden audio element for screen share audio */}
      {!isLocal && screenShareOn && screenShareAudioTrack && (
        <ScreenShareAudioPlayer track={screenShareAudioTrack} volume={screenShareVolume} />
      )}

      {/* Screen Share Info Overlay */}
      {compact ? (
        participant.picture ? (
          <div
            className={`absolute right-2 bottom-2 size-6 flex-shrink-0 overflow-hidden rounded-full`}
          >
            <img
              alt={displayName || t("common:participant")}
              className="h-full w-full object-cover"
              src={participant.picture || "/placeholder.svg"}
            />
          </div>
        ) : (
          <div
            className={`bg-background text-accent-foreground flex ${
              compact
                ? "h-12 w-12 text-lg"
                : "h-16 w-16 text-2xl max-[768px]:h-14 max-[768px]:w-14 max-[768px]:text-xl"
            } flex-shrink-0 items-center justify-center rounded-full font-medium`}
          >
            {displayName?.charAt(0)?.toUpperCase() || "?"}
          </div>
        )
      ) : (
        <div
          className={cn(
            "bg-background/80 text-accent-foreground absolute bottom-1 left-1 rounded-md px-3 py-1 text-xs font-medium",
          )}
        >
          <div className="flex items-center gap-2">
            <Icon height="12" icon="lucide:monitor" width="12" />
            <span>
              {displayName}, {t("classroom:screenShare")}
            </span>
          </div>
        </div>
      )}

      {!isLocal && screenShareOn && screenShareAudioTrack && !compact && (
        <div className={"absolute top-4 right-4"}>
          <Popover open={showVolumeSlider} onOpenChange={setShowVolumeSlider}>
            <PopoverTrigger asChild>
              <Button
                className={`bg-background/80 hover:bg-background/90 text-accent-foreground ${compact ? "size-6" : "size-8"} rounded-full shadow-sm`}
                size="icon"
                variant="ghost"
              >
                <Icon
                  height="16"
                  icon={
                    screenShareVolume === 0
                      ? "lucide:volume-x"
                      : screenShareVolume < 0.5
                        ? "lucide:volume-1"
                        : "lucide:volume-2"
                  }
                  width="16"
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-48 p-3" side="bottom">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-accent-foreground text-sm font-medium">
                    {t("common:audio")}
                  </span>
                  <Button className="h-5 w-5 p-0" size="icon" variant="ghost" onClick={toggleMute}>
                    <Icon
                      height="12"
                      icon={screenShareVolume === 0 ? "lucide:volume-x" : "lucide:volume-2"}
                      width="12"
                    />
                  </Button>
                </div>
                <div className="bg-muted/20 rounded-xl p-3 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-xs">
                      {t("common:currentValue")}
                    </span>
                    <span className={"text-accent-foreground flex items-center gap-1 text-xs"}>
                      <SlidingNumber value={Math.round(screenShareVolume * 100)} />
                      {"%"}
                    </span>
                  </div>

                  <Slider
                    className="py-3"
                    max={1}
                    min={0}
                    sliderClassName="bg-accent-foreground/75"
                    step={0.01}
                    thumbClassName="bg-accent-foreground ring-accent-foreground/50"
                    value={[screenShareVolume]}
                    onValueChange={handleVolumeChange}
                  />

                  <div className="flex justify-between">
                    <span className="text-muted-foreground text-xs">0%</span>
                    <span className="text-muted-foreground text-xs">100%</span>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}
